import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { Text, Card, Button, useTheme, Divider } from 'react-native-paper';
import {
  AdaptiveProgressBar,
  ProgressAdjustmentNotification,
  RealTimeProgressUpdater,
  ProgressMilestoneCelebration,
  ProgressAnalyticsDashboard,
} from '../components/ai';
import {
  useAdaptiveProgress,
  useProgressUpdates,
  useProgressAdjustments,
  useProgressCelebrations,
} from '../hooks/useAdaptiveProgress';
import type { ProgressAdjustment } from '../components/ai/AdaptiveProgressBar';
import type { MilestoneCelebration } from '../components/ai/ProgressMilestoneCelebration';
import type { LearningInsight } from '../components/ai/ProgressAnalyticsDashboard';
import { logger } from '../utils/logger';

export function AdaptiveProgressScreen() {
  const theme = useTheme();
  const { analytics, fetchAnalytics, loadingState } = useAdaptiveProgress();
  const { updates, recordUpdate, confirmUpdate, rejectUpdate, showOptimistic } =
    useProgressUpdates();
  const { adjustments, acceptAdjustment, dismissAdjustment } =
    useProgressAdjustments();
  const { celebrations, completeCelebration, celebrationsEnabled } =
    useProgressCelebrations();

  const [showDashboard, setShowDashboard] = useState(false);
  const [selectedAdjustment, setSelectedAdjustment] =
    useState<ProgressAdjustment | null>(null);
  const [selectedCelebration, setSelectedCelebration] =
    useState<MilestoneCelebration | null>(null);

  // Mock data for demonstration
  const mockAdjustments: ProgressAdjustment[] = [
    {
      id: 'adj_1',
      type: 'difficulty_change',
      reason:
        "Your recent performance suggests you're ready for more challenging content",
      impact: 0.15,
      confidence: 0.85,
      aiReasoning:
        'Based on your 92% success rate on recent exercises, increasing difficulty will maintain optimal challenge level and accelerate learning.',
      appliedAt: new Date().toISOString(),
    },
    {
      id: 'adj_2',
      type: 'timeline_extension',
      reason: 'Extending timeline to ensure thorough understanding',
      impact: -0.1,
      confidence: 0.78,
      aiReasoning:
        'Your learning pattern shows better retention with slightly longer practice periods. This adjustment prioritizes quality over speed.',
      appliedAt: new Date().toISOString(),
    },
  ];

  const mockCelebration: MilestoneCelebration = {
    id: 'celebration_1',
    userId: 'user_1',
    milestoneType: 'skill_mastery',
    title: 'Espresso Mastery Achieved!',
    description:
      "You've successfully mastered the fundamentals of espresso brewing",
    personalizedMessage:
      "Your dedication to consistent practice and attention to detail has paid off. You've shown exceptional progress in extraction timing and grind consistency.",
    achievementData: {
      skillName: 'Espresso Basics',
      newLevel: 'Advanced',
      currentValue: 95,
    },
    rewards: [
      {
        id: 'reward_1',
        type: 'badge',
        name: 'Espresso Expert',
        description: 'Master of espresso fundamentals',
        icon: '☕',
      },
      {
        id: 'reward_2',
        type: 'unlock',
        name: 'Advanced Techniques',
        description: 'Unlock advanced espresso techniques',
        icon: '🔓',
      },
    ],
    shareableContent: {
      text: 'Just mastered espresso brewing fundamentals! ☕ #CoffeeJourney #EspressoMaster',
      hashtags: ['CoffeeJourney', 'EspressoMaster', 'CoffeePlexity'],
      platforms: ['twitter', 'facebook', 'instagram'],
    },
    nextGoal: 'Master latte art techniques',
    celebrationLevel: 'large',
    timestamp: new Date().toISOString(),
  };

  useEffect(() => {
    // Fetch analytics on component mount
    fetchAnalytics({ userId: 'current-user', timeRange: '30d' });
  }, [fetchAnalytics]);

  const handleSimulateProgress = () => {
    recordUpdate({
      skillId: 'espresso-basics',
      skillName: 'Espresso Basics',
      oldProgress: 0.75,
      newProgress: 0.82,
      confidence: 0.9,
      source: 'user_action',
      optimistic: true,
    });
  };

  const handleSimulateAdjustment = () => {
    if (mockAdjustments[0]) {
      setSelectedAdjustment(mockAdjustments[0]);
    }
  };

  const handleSimulateCelebration = () => {
    setSelectedCelebration(mockCelebration);
  };

  const handleAdjustmentAccept = () => {
    if (selectedAdjustment) {
      acceptAdjustment(selectedAdjustment.id);
      setSelectedAdjustment(null);
    }
  };

  const handleAdjustmentDismiss = () => {
    if (selectedAdjustment) {
      dismissAdjustment(selectedAdjustment.id);
      setSelectedAdjustment(null);
    }
  };

  const handleCelebrationComplete = () => {
    if (selectedCelebration) {
      completeCelebration(selectedCelebration.id);
      setSelectedCelebration(null);
    }
  };

  const handleInsightAction = (insight: LearningInsight) => {
    logger.log('Taking action on insight:', insight);
    // Implement insight action logic
  };

  return (
    <View
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.content}
      >
        {/* Header */}
        <Card style={styles.headerCard} mode='outlined'>
          <Card.Content>
            <Text variant='headlineSmall' style={styles.title}>
              Adaptive Progress Indicators
            </Text>
            <Text variant='bodyMedium' style={styles.subtitle}>
              Demonstration of AI-powered progress tracking components
            </Text>
          </Card.Content>
        </Card>

        {/* Demo Controls */}
        <Card style={styles.controlsCard} mode='outlined'>
          <Card.Content>
            <Text variant='titleMedium' style={styles.sectionTitle}>
              Demo Controls
            </Text>

            <View style={styles.buttonRow}>
              <Button
                mode='outlined'
                onPress={handleSimulateProgress}
                style={styles.demoButton}
                icon='trending-up'
              >
                Simulate Progress
              </Button>

              <Button
                mode='outlined'
                onPress={handleSimulateAdjustment}
                style={styles.demoButton}
                icon='brain'
              >
                Show Adjustment
              </Button>
            </View>

            <View style={styles.buttonRow}>
              <Button
                mode='outlined'
                onPress={handleSimulateCelebration}
                style={styles.demoButton}
                icon='trophy'
              >
                Trigger Celebration
              </Button>

              <Button
                mode='outlined'
                onPress={() => setShowDashboard(!showDashboard)}
                style={styles.demoButton}
                icon='chart-line'
              >
                {showDashboard ? 'Hide' : 'Show'} Analytics
              </Button>
            </View>
          </Card.Content>
        </Card>

        {/* Adaptive Progress Bars */}
        <Card style={styles.componentCard} mode='outlined'>
          <Card.Content>
            <Text variant='titleMedium' style={styles.sectionTitle}>
              Adaptive Progress Bars
            </Text>

            <AdaptiveProgressBar
              currentProgress={0.75}
              predictedProgress={0.85}
              targetProgress={0.9}
              aiAdjustments={mockAdjustments}
              label='Espresso Mastery'
              sublabel='Current skill level'
              onAdjustmentInfo={adjustment =>
                logger.log('Adjustment info:', adjustment)
              }
            />

            <AdaptiveProgressBar
              currentProgress={0.45}
              predictedProgress={0.52}
              aiAdjustments={[]}
              label='Latte Art'
              sublabel='Developing skill'
              showPrediction={true}
            />

            <AdaptiveProgressBar
              currentProgress={0.92}
              predictedProgress={0.95}
              aiAdjustments={mockAdjustments[1] ? [mockAdjustments[1]] : []}
              label='Coffee Theory'
              sublabel='Near mastery'
            />
          </Card.Content>
        </Card>

        <Divider style={styles.divider} />

        {/* Real-time Updates Display */}
        <Card style={styles.componentCard} mode='outlined'>
          <Card.Content>
            <Text variant='titleMedium' style={styles.sectionTitle}>
              Real-time Progress Updates
            </Text>
            <Text variant='bodySmall' style={styles.description}>
              Updates appear in the top-right corner when progress changes occur
            </Text>
          </Card.Content>
        </Card>

        <Divider style={styles.divider} />

        {/* Analytics Dashboard */}
        {showDashboard && analytics && (
          <Card style={styles.dashboardCard} mode='outlined'>
            <Card.Content>
              <Text variant='titleMedium' style={styles.sectionTitle}>
                Progress Analytics Dashboard
              </Text>

              <ProgressAnalyticsDashboard
                analytics={analytics}
                onTimeRangeChange={range =>
                  fetchAnalytics({ userId: 'current-user', timeRange: range })
                }
                onInsightAction={handleInsightAction}
                onSkillSelect={skillId =>
                  logger.log('Selected skill:', skillId)
                }
              />
            </Card.Content>
          </Card>
        )}

        {/* Loading State */}
        {loadingState.isLoading && (
          <Card style={styles.statusCard} mode='outlined'>
            <Card.Content>
              <Text variant='bodyMedium'>Loading analytics...</Text>
            </Card.Content>
          </Card>
        )}

        {/* Error State */}
        {loadingState.error && (
          <Card style={styles.errorCard} mode='outlined'>
            <Card.Content>
              <Text variant='bodyMedium' style={styles.errorText}>
                Error: {loadingState.error}
              </Text>
            </Card.Content>
          </Card>
        )}
      </ScrollView>

      {/* Real-time Progress Updater */}
      <RealTimeProgressUpdater
        updates={updates}
        onUpdateConfirmed={confirmUpdate}
        onUpdateRejected={rejectUpdate}
        showOptimisticUpdates={showOptimistic}
      />

      {/* Progress Adjustment Notification */}
      {selectedAdjustment && (
        <ProgressAdjustmentNotification
          adjustment={selectedAdjustment}
          onAccept={handleAdjustmentAccept}
          onDismiss={handleAdjustmentDismiss}
          onViewDetails={() => logger.log('View adjustment details')}
        />
      )}

      {/* Milestone Celebration */}
      {selectedCelebration && celebrationsEnabled && (
        <ProgressMilestoneCelebration
          milestone={selectedCelebration}
          onCelebrationComplete={handleCelebrationComplete}
          onShare={(platform, content) =>
            logger.log('Share on', platform, content)
          }
          onViewRewards={rewards => logger.log('View rewards', rewards)}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 16,
    paddingBottom: 100, // Extra space for floating components
  },
  headerCard: {
    marginBottom: 16,
  },
  title: {
    fontWeight: '600',
    marginBottom: 8,
  },
  subtitle: {
    opacity: 0.7,
  },
  controlsCard: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontWeight: '600',
    marginBottom: 12,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 12,
  },
  demoButton: {
    flex: 1,
  },
  componentCard: {
    marginBottom: 16,
  },
  dashboardCard: {
    marginBottom: 16,
  },
  statusCard: {
    marginBottom: 16,
    backgroundColor: '#E3F2FD',
  },
  errorCard: {
    marginBottom: 16,
    backgroundColor: '#FFEBEE',
  },
  errorText: {
    color: '#D32F2F',
  },
  description: {
    opacity: 0.7,
    fontStyle: 'italic',
  },
  divider: {
    marginVertical: 16,
  },
});
